// TODO - rewrite not to use refs

import * as React from "react";

import { Compo } from "../models/compos";
import { api } from "../api";
import { User } from "../models/users";
import { useUser } from "../state";
import { EntryList } from "../DesignSystem/EntryList";
import { GoldCoin } from "../Toolbar/GoldCoin";
import { RouteComponentProps, Redirect } from "react-router";
import { Countdown } from "../DesignSystem/Countdown";
import { API_CompoId } from "../../../src/apidefs";
import { EntryJSON } from "../models/entries";
import Tooltip from "../DesignSystem/Tooltip";
import { Util } from "../util";
import { RLink } from "../DesignSystem/RLink";
import {
  JudgedExplainer,
  OptionalThemeExplainer,
} from "../Entry/EditEntryPage";
import { SmallButton } from "../DesignSystem/SmallButton";
import { Button } from "../DesignSystem/Button";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import { TestIds } from "../testIds";
import { CollaboratorsInput } from "../DesignSystem/CollaboratorsInput";
import { SecretSantaInput } from "../DesignSystem/SecretSantaInput";

export type EntryType = "song" | "story" | "art";
export const EntryTypes: EntryType[] = ["song", "story", "art"];

interface SubmissionProps {
  compo: Compo;
  entriesSubmitted: { [key in EntryType]: number };
  onCreateEntry: (entry: EntryJSON) => void;
  user: User | undefined;
}

interface SubmissionState {
  submittedEntry?: EntryJSON;
  type: EntryType;

  followedTheme: boolean;
  isJudged: boolean;
  isJoke: boolean;
  collaborators: string[];

  // Secret Santa fields
  isSecretSantaTrack: boolean;
  secretSantaRecipient: string;
  secretSantaPromptText: string;
}

export function SubmissionSection(
  props: Omit<SubmissionProps, "user">
): JSX.Element {
  const userState = useUser();
  // Extract User from UserState for the inner component
  const user = userState.type === "logged-in" ? userState.user : undefined;
  return <SubmissionSectionInner {...props} user={user} />;
}

class SubmissionSectionInner extends React.Component<
  SubmissionProps,
  SubmissionState
> {
  downloadable: HTMLInputElement | undefined;
  fileInput: HTMLInputElement | undefined;
  title: HTMLInputElement | undefined;
  tags: HTMLInputElement | undefined;
  description: HTMLTextAreaElement | undefined;
  statusThing: HTMLSpanElement | undefined;
  uploadedFileUrl: HTMLInputElement | undefined;
  submitButton: HTMLButtonElement | undefined;
  loadingSpinner: HTMLSpanElement | undefined;

  constructor(props: SubmissionProps) {
    super(props);
    this.state = {
      followedTheme: true,
      isJoke: false,
      type: "song",
      isJudged: true,
      collaborators: [props.user?.username || "", "", "", "", "", ""],
      isSecretSantaTrack: props.compo.is_secret_santa,
      secretSantaRecipient: "",
      secretSantaPromptText: "",
    };
  }

  setType(type: EntryType): void {
    this.setState({ type });
  }

  changeFileInput(): void {
    if (!this.fileInput) {
      return;
    }

    const files = this.fileInput.files;
    const file = files && files[0];

    if (file == null) {
      return alert("No file selected.");
    }

    this.getSignedRequest(file);
  }

  getSignedRequest(file: File) {
    const xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      "/api/sign-s3?file-name=" + file.name + "&file-type=" + file.type
    );

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);

          this.uploadFile(file, response.signedRequest, response.url);
        } else {
          alert("Upload failed for some reason.");
        }
      }
    };

    xhr.send();
  }

  uploadFile(file: File, signedRequest: string, url: string): void {
    if (!this.statusThing) {
      return;
    }

    const xhr = new XMLHttpRequest();

    this.statusThing.innerHTML = "Uploading... please wait!";

    xhr.open("PUT", signedRequest);

    xhr.onreadystatechange = () => {
      if (!this.statusThing || !this.uploadedFileUrl || !this.submitButton) {
        return;
      }

      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          this.statusThing.innerHTML = "Done uploading!";
          this.uploadedFileUrl.value = url;
          this.submitButton.disabled = false;
        } else {
          this.statusThing.innerHTML = "Could not upload file.";
        }
      }
    };

    xhr.send(file);
  }

  async submit(): Promise<void> {
    if (
      !this.title ||
      !this.description ||
      !this.uploadedFileUrl ||
      !this.tags
    ) {
      return;
    }

    const results = await api.post<"/api/compo/:compo_id/submit">(
      `/api/compo/${this.props.compo.id}/submit`,
      {
        title: this.title.value,
        description: this.description.value,
        tags: this.tags.value,
        type: this.state.type,
        followed_theme: this.state.followedTheme,
        isJoke: this.state.isJoke,
        isJudged: this.state.isJudged,
        downloadable: (this.downloadable && this.downloadable.checked) || true,
        "uploaded-file-url": this.uploadedFileUrl.value,
        collaborator_0: this.state.collaborators[0] || undefined,
        collaborator_1: this.state.collaborators[1] || undefined,
        collaborator_2: this.state.collaborators[2] || undefined,
        collaborator_3: this.state.collaborators[3] || undefined,
        collaborator_4: this.state.collaborators[4] || undefined,
        collaborator_5: this.state.collaborators[5] || undefined,
        secret_santa_recipient:
          this.state.isSecretSantaTrack && this.state.secretSantaRecipient
            ? this.state.secretSantaRecipient
            : undefined,
        secret_santa_prompt_text:
          this.state.isSecretSantaTrack && this.state.secretSantaPromptText
            ? this.state.secretSantaPromptText
            : undefined,
      }
    );

    if (api.isErrorToast(results)) {
      return;
    }

    this.setState({
      submittedEntry: results.data!,
    });

    if (results.data) {
      this.props.onCreateEntry(results.data);
    }
  }

  render(): JSX.Element {
    if (this.state.submittedEntry) {
      return (
        <div className="mx-auto max-w-4xl px-4 py-16">
          <Redirect push to={`/entry/${this.state.submittedEntry.id}`} />
        </div>
      );
    }

    const canSubmit =
      this.props.entriesSubmitted[this.state.type] === 0 ||
      this.props.compo.nogold ||
      (this.props.user && this.props.user.coins > 100) ||
      !this.state.isJudged;

    const submitButton = (
      <button
        id="submitEntryBtn"
        ref={(e) => {
          if (e) this.submitButton = e;
        }}
        className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-6 py-3 font-semibold text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={!canSubmit}
        onClick={() => this.submit()}
        data-testid={TestIds.EntrySubmitButton}
      >
        Submit {this.state.type}!
        {this.props.entriesSubmitted[this.state.type] === 0 ||
        this.props.compo.nogold ||
        !this.state.isJudged ? undefined : (
          <span className="text-xs">
            (-10 <GoldCoin />)
          </span>
        )}
        <span
          ref={(r) => {
            if (r) this.loadingSpinner = r;
          }}
          id="loading-spinner"
          style={{ display: "none" }}
        >
          <i className="fa fa-spinner fa-pulse"></i>
        </span>
      </button>
    );

    return (
      <div className="mx-auto max-w-4xl px-4 py-16">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold text-slate-900 dark:text-white">
            Submit an Entry!
          </h1>
          <p className="mt-2 text-slate-500 dark:text-slate-400">
            To {this.props.compo.name}
          </p>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <div className="mb-6">
              <label className="mb-3 block text-sm font-medium text-slate-700 dark:text-slate-300">
                What type of entry?
              </label>
              <div className="flex gap-3">
                {this.props.compo.types.indexOf("song") !== -1 && (
                  <button
                    type="button"
                    onClick={() => this.setType("song")}
                    className={`rounded-lg px-4 py-2 font-medium transition ${
                      this.state.type === "song"
                        ? "bg-chips-gold text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    }`}
                  >
                    Song
                  </button>
                )}
                {this.props.compo.types.indexOf("story") !== -1 && (
                  <button
                    type="button"
                    onClick={() => this.setType("story")}
                    data-testid={TestIds.EntryTypeStory}
                    className={`rounded-lg px-4 py-2 font-medium transition ${
                      this.state.type === "story"
                        ? "bg-chips-gold text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    }`}
                  >
                    Story
                  </button>
                )}
                {this.props.compo.types.indexOf("art") !== -1 && (
                  <button
                    type="button"
                    onClick={() => this.setType("art")}
                    className={`rounded-lg px-4 py-2 font-medium transition ${
                      this.state.type === "art"
                        ? "bg-chips-gold text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    }`}
                  >
                    Art
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <input
                  ref={(r) => {
                    if (r) this.title = r;
                  }}
                  type="text"
                  name="title"
                  placeholder="title"
                  data-testid={TestIds.EntryTitleInput}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                />
              </div>

              <div>
                <textarea
                  ref={(r) => {
                    if (r) this.description = r;
                  }}
                  name="description"
                  rows={6}
                  placeholder={
                    this.state.type === "story"
                      ? `Write your story here.`
                      : `Describe your ${this.state.type}!\n\nRemember, submissions are anonymous by default. You don't want to give anything away!`
                  }
                  data-testid={TestIds.EntryDescriptionInput}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                />
              </div>

              <div>
                <input
                  ref={(r) => {
                    if (r) this.tags = r;
                  }}
                  type="text"
                  name="tags"
                  placeholder="genre tags (e.g. 'chiptune, video game')"
                  data-testid={TestIds.EntryTagsInput}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                />
              </div>

              <CollaboratorsInput
                values={this.state.collaborators}
                onChange={(collaborators) => this.setState({ collaborators })}
              />

              {this.props.compo.is_secret_santa && (
                <div className="rounded-lg border-2 border-slate-600 bg-slate-50 p-4 dark:border-purple-300 dark:bg-slate-800/50">
                  <SecretSantaInput
                    isSecretSantaTrack={this.state.isSecretSantaTrack}
                    onIsSecretSantaTrackChange={(value) =>
                      this.setState({ isSecretSantaTrack: value })
                    }
                    secretSantaRecipient={this.state.secretSantaRecipient}
                    onSecretSantaRecipientChange={(value) =>
                      this.setState({ secretSantaRecipient: value })
                    }
                    secretSantaPromptText={this.state.secretSantaPromptText}
                    onSecretSantaPromptTextChange={(value) =>
                      this.setState({ secretSantaPromptText: value })
                    }
                  />
                </div>
              )}

              {(this.state.type === "song" || this.state.type === "art") && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    <span
                      id="statusThing"
                      ref={(r) => {
                        if (r) this.statusThing = r;
                      }}
                    >
                      Upload: Select{" "}
                      {this.state.type === "song"
                        ? "an MP3"
                        : "a PNG, GIF or JPEG."}
                    </span>
                  </label>
                  <input
                    type="file"
                    id="file-input"
                    onChange={(_e) => this.changeFileInput()}
                    ref={(r) => {
                      if (r) this.fileInput = r;
                    }}
                    className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 file:mr-4 file:rounded-md file:border-0 file:bg-chips-gold file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-amber-600 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
                  />
                  <input
                    type="hidden"
                    id="uploaded-file-url"
                    name="uploaded-file-url"
                    ref={(e) => {
                      if (e) this.uploadedFileUrl = e;
                    }}
                    value=""
                  />
                </div>
              )}

              {this.state.type === "song" && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="downloadable"
                    ref={(d) => (this.downloadable = d ? d : undefined)}
                    name="downloadable"
                    defaultChecked={true}
                    className="h-4 w-4 rounded border-slate-300 text-chips-gold focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10"
                  />
                  <label
                    htmlFor="downloadable"
                    className="text-sm text-slate-700 dark:text-slate-300"
                  >
                    Allow downloads.
                  </label>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="followed_theme"
                  name="followed_theme"
                  onChange={(e) =>
                    this.setState({ followedTheme: e.currentTarget.checked })
                  }
                  defaultChecked={this.state.followedTheme}
                  className="h-4 w-4 rounded border-slate-300 text-chips-gold focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10"
                />
                <Tooltip
                  element={
                    <label
                      htmlFor="followed_theme"
                      className="text-sm text-slate-700 dark:text-slate-300"
                    >
                      I followed the theme.
                    </label>
                  }
                  tooltip={<OptionalThemeExplainer />}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_judged"
                  name="is_judged"
                  onChange={(e) =>
                    this.setState({
                      isJudged: e.currentTarget.checked,
                      isJoke: !e.currentTarget.checked
                        ? this.state.isJoke
                        : false,
                    })
                  }
                  checked={this.state.isJudged}
                  className="h-4 w-4 rounded border-slate-300 text-chips-gold focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10"
                />
                <Tooltip
                  element={
                    <label
                      htmlFor="is_judged"
                      className="text-sm text-slate-700 dark:text-slate-300"
                    >
                      I want to get scores.
                    </label>
                  }
                  tooltip={<JudgedExplainer />}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_joke"
                  name="is_joke"
                  onChange={(e) =>
                    this.setState({
                      isJoke: e.currentTarget.checked,
                      isJudged: e.currentTarget.checked
                        ? false
                        : this.state.isJudged,
                    })
                  }
                  checked={this.state.isJoke}
                  className="h-4 w-4 rounded border-slate-300 text-chips-gold focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10"
                />
                <Tooltip
                  element={
                    <label
                      htmlFor="is_joke"
                      className="text-sm text-slate-700 dark:text-slate-300"
                    >
                      This is a joke or meme track.
                    </label>
                  }
                  tooltip={
                    <div>
                      <div className="pb-[10px] text-[18px]">
                        Is this a joke track?
                      </div>

                      <div>
                        If it is, it won't be voted on or played in the sync
                        listen.
                      </div>
                    </div>
                  }
                />
              </div>

              <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
                {this.props.compo.nogold ? (
                  <span>All entries to this contest are free! Go crazy!</span>
                ) : (
                  <span>
                    {this.state.isJudged ? (
                      <span>
                        You get one free entry of each type. After that,{" "}
                        <RLink to="/explain-gold">
                          they're 10 <GoldCoin />.
                        </RLink>{" "}
                      </span>
                    ) : (
                      <span>All unjudged entries are free.</span>
                    )}
                  </span>
                )}{" "}
                <RLink to="/explain-everything">Read the full rules.</RLink>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            {canSubmit ? (
              submitButton
            ) : (
              <Tooltip
                element={submitButton}
                tooltip={
                  <div>
                    You don't have enough gold to submit an additional entry.
                    (But if you disable judging, it will be free.)
                  </div>
                }
              />
            )}
          </div>
        </div>

        <script
          dangerouslySetInnerHTML={{
            __html: `
            setTimeout(() => {
              document.getElementById("file-input").onchange = () => {
            }, 500);
          `,
          }}
        />
      </div>
    );
  }
}

interface CompoPageProps extends RouteComponentProps<{ id: string }> {
  user: User | undefined;
}

interface CompoPageState {
  compo?: API_CompoId;
  error: boolean;
}

export function CompoPage(
  props: RouteComponentProps<{ id: string }>
): JSX.Element {
  const userState = useUser();
  // Extract User from UserState for the inner component
  const user = userState.type === "logged-in" ? userState.user : undefined;
  return <CompoPageInner {...props} user={user} />;
}

class CompoPageInner extends React.Component<CompoPageProps, CompoPageState> {
  state: CompoPageState = {
    compo: undefined,
    error: false,
  };

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
  }

  async componentWillReceiveProps(newProps: CompoPageProps): Promise<void> {
    const compoId = Number(newProps.match.params.id);
    const res = await api.get<"/api/compo/:id">(`/api/compo/${compoId}`);

    if (api.isToast(res)) {
      this.setState({ error: true });

      return;
    }

    this.setState({
      compo: res,
    });
  }

  hasStarted(): boolean {
    if (!this.state.compo) {
      return false;
    }

    const start = new Date(this.state.compo.compo.start);

    return new Date() > start;
  }

  render(): JSX.Element {
    if (!this.state.compo) {
      return <div>Loading</div>;
    }

    const compoObj = new Compo(this.state.compo.compo);

    let countdown: JSX.Element | undefined;
    let endDate: Date | undefined;

    if (new Date() < new Date(this.state.compo.compo.start)) {
      countdown = (
        <Countdown until={compoObj.startDate} text="until it starts!" />
      );
      endDate = new Compo(this.state.compo.compo).startDate;
    } else if (new Date() < new Date(this.state.compo.compo.stop)) {
      countdown = <Countdown until={compoObj.stopDate} text="until it ends!" />;
      endDate = new Compo(this.state.compo.compo).stopDate;
    }

    const theme_short = this.state.compo.compo.theme_short;
    const buttonClasses =
      "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

    return (
      <div>
        <div className="border-b border-slate-200 bg-white px-4 py-8 dark:border-slate-700 dark:bg-slate-800 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <h1 className="flex flex-wrap items-center gap-3 text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">
              {this.state.compo.compo.name}
              {this.props.user && this.props.user.admin && (
                <RLink to={`/compo/${this.props.match.params.id}/admin`}>
                  <SmallButton
                    variant="mod"
                    icon={<ShieldCheckIcon className="h-4 w-4" />}
                  >
                    Admin
                  </SmallButton>
                </RLink>
              )}
            </h1>
            {theme_short && (
              <p className="mt-2 text-lg text-slate-600 dark:text-slate-400">
                Theme: {theme_short}
              </p>
            )}
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-slate-900">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-8 lg:flex-row">
              {/* Left Column - Submissions */}
              <div className="flex-1 lg:flex-[2]">
                {this.state.compo && compoObj.state !== "before" ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <h2 className="mb-4 text-xl font-semibold text-[#333] dark:text-white">
                      Submissions ({this.state.compo.entries.length})
                    </h2>
                    <EntryList
                      favoriteEntryIDs={this.state.compo.favoriteEntryIDs}
                      commentedEntryIDs={this.state.compo.commentedEntryIDs}
                      entries={this.state.compo.entries}
                      avatar_urls={this.state.compo.avatarUrls}
                      hideStats
                    />
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <h2 className="mb-2 text-xl font-semibold text-[#333] dark:text-white">
                      Waiting to start...
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400">
                      The competition hasn't started yet. The theme will be
                      revealed when the countdown hits zero.
                    </p>
                    <p className="mt-4 text-slate-500 dark:text-slate-400">
                      In the meantime, hang out with other compo-ers in our{" "}
                      <RLink to="https://discord.gg/AUvDuUkxXr">Discord!</RLink>
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column - Theme & Actions */}
              <div className="space-y-4 lg:w-80">
                {/* Theme Card */}
                {compoObj.state !== "before" && (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <h3 className="mb-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                      Theme
                    </h3>
                    <div
                      className="text-slate-700 dark:text-slate-300"
                      dangerouslySetInnerHTML={{
                        __html: this.state.compo.compo.theme,
                      }}
                    />
                  </div>
                )}

                {/* Instructions Card */}
                {this.state.compo.compo.description &&
                  compoObj.state !== "before" && (
                    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                      <h3 className="mb-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                        Instructions
                      </h3>
                      <div
                        className="text-sm text-slate-600 dark:text-slate-400"
                        dangerouslySetInnerHTML={{
                          __html: this.state.compo.compo.description,
                        }}
                      />
                    </div>
                  )}

                {/* Sign Up Card */}
                {!this.props.user && (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <p className="mb-3 text-slate-600 dark:text-slate-400">
                      You need to sign up to participate!
                    </p>
                    <RLink to="/register" className={buttonClasses}>
                      Sign up!
                    </RLink>
                  </div>
                )}

                {/* Countdown & Actions Card */}
                <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                  {compoObj.state === "before" && compoObj.allow_voting && (
                    <>
                      <p className="mb-3 text-slate-600 dark:text-slate-400">
                        Before the contest starts, you can help pick out a
                        theme.
                      </p>
                      <RLink
                        to={`/compo/${this.props.match.params.id}/judge`}
                        className={buttonClasses}
                      >
                        Vote on themes!
                      </RLink>
                      <div className="mt-4">{countdown}</div>
                    </>
                  )}

                  {compoObj.state === "before" && !compoObj.allow_voting && (
                    <div className="text-center">{countdown}</div>
                  )}

                  {countdown && endDate && compoObj.state !== "before" && (
                    <>
                      <div className="mb-2">{countdown}</div>
                      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
                        That's {Util.FormatDate(new Date(endDate))}
                      </p>
                      {this.state.compo &&
                        (compoObj.state === "entry" ||
                          (this.props.user && this.props.user.admin)) && (
                          <Button
                            to={`/compo/${this.state.compo.compo.id}/submit`}
                            variant="primary"
                            grow={false}
                          >
                            Submit an entry!
                          </Button>
                        )}
                    </>
                  )}

                  {this.state.compo.can_judge && (
                    <>
                      <p className="mb-3 text-slate-600 dark:text-slate-400">
                        Submissions are over, time to vote!
                      </p>
                      <RLink
                        to={`/compo/${this.props.match.params.id}/judge`}
                        className={buttonClasses}
                      >
                        Go judge!
                      </RLink>
                    </>
                  )}

                  {compoObj.state === "over" && (
                    <>
                      <p className="mb-3 text-slate-600 dark:text-slate-400">
                        Competition has finished!
                      </p>
                      <RLink
                        to={`/compo/${this.props.match.params.id}/results`}
                        className={buttonClasses}
                      >
                        See results!
                      </RLink>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
